import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  CONSTS,
  FILE_NAME,
  ReconcileReportFieldFormGroup,
} from './reconcile-report.constants';

@Component({
  selector: 'app-reconcile-report',
  templateUrl: './reconcile-report.component.html',
  styleUrls: ['./reconcile-report.component.scss'],
  standalone: false,
})
export class ReconcileReportComponent implements OnInit {
  destroy = inject(DestroyRef);
  userService = inject(UserService);
  input: ReconcileReportFieldFormGroup = inject(MAT_DIALOG_DATA);
  private dialogRef: MatDialogRef<ReconcileReportComponent> = inject(
    MatDialogRef<ReconcileReportComponent>,
  );
  private codReconcileReportService: CodsReportsService =
    inject(CodsReportsService);
  private fb: FormBuilder = inject(FormBuilder);
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  reconcileReportForm!: FormGroup;

  locationCode = '';
  routes: IOption[] = [];
  currDate: Date = new Date();
  minDate: Date = new Date(
    this.currDate.getFullYear(),
    this.currDate.getMonth(),
    this.currDate.getDate() - 60,
  );
  maxDate: Date = new Date(
    this.currDate.getFullYear(),
    this.currDate.getMonth(),
    this.currDate.getDate() + 14,
  );

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.reconcileReportForm = this.fb.group({
            fromDate: [this.currDate],
            toDate: [this.currDate],
            routes: [this.routes],
          });
          return this.getRouteList(user.location);
        }),
      )
      .subscribe();
  }

  /**
   * To get route list
   *
   * @memberof ReconcileReportComponent
   */
  getRouteList(locationCode: string): Observable<void> {
    return (
      this.codReconcileReportService
        //  update location code
        .getReconcileRoutes(locationCode)
        .pipe(
          take(1),
          map((data) => {
            this.routes = data.response.CODReconcileRoutes.map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            }).sort((a, b) => a.label.localeCompare(b.label));
            this.routes.unshift({
              label: $localize`All`,
              value: $localize`All`,
            });
            this.reconcileReportForm
              .get('routes')
              ?.setValue(this.routes[0].value);
          }),
        )
    );
  }

  getReport() {
    const formValue = this.reconcileReportForm.value;

    const fromDate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const request = {
            locationCode: user?.location,
            fromDate: fromDate,
            toDate: toDate,
            route: formValue.route?.value === 'All' ? 'All' : formValue.routes,
          };

          return this.codReconcileReportService
            .getCODReconcileReport(request)
            .pipe(
              take(1),
              map((data) => {
                const codReconcileReport = data.response.CODReconcile;

                if (codReconcileReport?.length) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      `${'COD Reconcile Report'}`,
                      '',
                      '',
                      '',
                      '',
                      '',
                    ],
                  ];
                  this.reportUtils.exportCSVReport(
                    codReconcileReport,
                    FILE_NAME,
                    customHeader,
                  );
                } else {
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.NO_DATA_AVAILABLE,
                  );
                }

                this.dialogRef.close();
              }),
            );
        }),
      )
      .subscribe();
  }

  clickExit() {
    this.commonUtils.openExitModal(this.reconcileReportForm.dirty);
  }
}
